import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { salesService, sitesService, beverageProductsService, stocktakingSettingsService, stocktakeSessionsService } from '../services';
import { useSettings } from '../contexts/SettingsContext';
import ProductPicker from '../components/ProductPicker';
import SitePicker from '../components/SitePicker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import {
  Save, Loader2, Trash2, Plus, ArrowLeft, CheckCircle2, Undo2,
  ShoppingCart, Info
} from 'lucide-react';
import { getApiError } from '@/lib/utils';

const emptyLine = () => ({
  beverage_product_id: '', quantity: '', unit_selling_price: '', unit_label: '',
  measure_ml: '', notes: '',
});

export default function SalesEntryPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currencySymbol } = useSettings();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [posting, setPosting] = useState(false);
  const [saveAndPosting, setSaveAndPosting] = useState(false);
  const [sale, setSale] = useState(null);

  const [header, setHeader] = useState({
    site_id: '', reference_number: '',
    sales_date: new Date().toISOString().split('T')[0],
    period_from: '', period_to: '', notes: '',
  });
  const [lines, setLines] = useState([emptyLine()]);

  const [sites, setSites] = useState([]);
  const [products, setProducts] = useState([]);
  const [settings, setSettings] = useState({ default_vat_rate: 20, invoice_basis: 'net', sales_price_basis: 'gross' });

  const isPosted = sale?.status === 'posted';

  // Product cost cache: { "productId:siteId" -> { cost_per_stock_unit, cost_source, stock_unit_label } }
  const [costCache, setCostCache] = useState({});
  const costFetchingRef = useRef({});

  // Load lookup data
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [sitesRes, prodsRes, settingsRes] = await Promise.all([
          sitesService.list({ limit: 500 }),
          beverageProductsService.listAll({ status: 'active' }),
          stocktakingSettingsService.get().catch(() => ({ default_vat_rate: 20, invoice_basis: 'net' })),
        ]);
        if (cancelled) return;
        setSites(Array.isArray(sitesRes) ? sitesRes : (sitesRes?.items || sitesRes?.sites || []));
        setProducts(Array.isArray(prodsRes) ? prodsRes : (prodsRes?.items || prodsRes?.products || []));
        setSettings(settingsRes);
      } catch {}
    };
    load();
    return () => { cancelled = true; };
  }, []);

  // Load existing sale
  useEffect(() => {
    if (isNew) return;
    let cancelled = false;
    const load = async () => {
      try {
        const data = await salesService.get(Number(id));
        if (cancelled) return;
        setSale(data);
        setHeader({
          site_id: String(data.site_id),
          reference_number: data.reference_number || '',
          sales_date: data.sales_date || '',
          period_from: data.period_from || '',
          period_to: data.period_to || '',
          notes: data.notes || '',
        });
        if (data.lines?.length > 0) {
          setLines(data.lines.map(ln => ({
            beverage_product_id: String(ln.beverage_product_id),
            quantity: String(ln.quantity),
            unit_selling_price: String(ln.unit_selling_price),
            unit_label: ln.unit_label || '',
            measure_ml: ln.measure_ml ? String(ln.measure_ml) : '',
            notes: ln.notes || '',
          })));
        }
      } catch {
        toast({ title: 'Error', description: 'Failed to load sale', variant: 'destructive' });
      } finally { setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [id, isNew, toast]);

  const productMap = useMemo(() => {
    const map = {};
    products.forEach(p => { map[p.id] = p; });
    return map;
  }, [products]);

  // Fetch product cost from backend and cache it
  const fetchProductCost = useCallback(async (productId, siteId) => {
    if (!productId || !siteId) return;
    const key = `${productId}:${siteId}`;
    if (costCache[key] || costFetchingRef.current[key]) return;
    costFetchingRef.current[key] = true;
    try {
      const data = await stocktakeSessionsService.lookupProductCost(
        Number(productId), Number(siteId), 'weighted_average', header.sales_date || null
      );
      setCostCache(prev => ({ ...prev, [key]: data }));
    } catch {
      setCostCache(prev => ({ ...prev, [key]: { cost_per_stock_unit: 0, cost_source: 'no_cost', stock_unit_label: 'unit' } }));
    } finally {
      delete costFetchingRef.current[key];
    }
  }, [costCache, header.sales_date]);

  // Auto-fetch costs when lines or site change
  useEffect(() => {
    if (!header.site_id) return;
    lines.forEach(ln => {
      if (ln.beverage_product_id) {
        fetchProductCost(ln.beverage_product_id, header.site_id);
      }
    });
  }, [lines, header.site_id, fetchProductCost]);

  // Get unit options for a product — smart unit selection
  const getUnitOptions = (productId) => {
    if (!productId) return [];
    const prod = productMap[parseInt(productId)];
    if (!prod || !prod.units?.length) return [];
    return prod.units.map(u => {
      let label = u.unit_type.charAt(0).toUpperCase() + u.unit_type.slice(1);
      if (u.conversion_to_base && u.unit_type !== 'case') {
        label += ` ${u.conversion_to_base}ml`;
      }
      if (u.is_default_count_unit) label += ' (default)';
      return { value: u.unit_type, label, isDefault: u.is_default_count_unit, conversion: u.conversion_to_base || 0 };
    });
  };

  // Get the best default selling unit for a product
  const getDefaultSellingUnit = (prod) => {
    if (!prod?.units?.length) return { unit: '', measure: '' };
    const countUnit = prod.units.find(u => u.is_default_count_unit);
    if (countUnit) return { unit: countUnit.unit_type, measure: countUnit.conversion_to_base ? String(countUnit.conversion_to_base) : '' };
    const stockUnit = prod.units.find(u => u.is_base_stock_unit);
    if (stockUnit) return { unit: stockUnit.unit_type, measure: stockUnit.conversion_to_base ? String(stockUnit.conversion_to_base) : '' };
    const first = prod.units[0];
    return { unit: first.unit_type, measure: first.conversion_to_base ? String(first.conversion_to_base) : '' };
  };

  // Get the base/stock unit for a product
  const getStockUnit = (prod) => {
    if (!prod?.units?.length) return null;
    return prod.units.find(u => u.is_base_stock_unit)
      || prod.units.find(u => u.is_default_count_unit)
      || prod.units[0];
  };

  // Compute conversion note (e.g. "= 0.80 Bottles")
  const getConversionNote = (ln) => {
    if (!ln.beverage_product_id) return null;
    const prod = productMap[parseInt(ln.beverage_product_id)];
    if (!prod || !prod.units?.length) return null;
    const stockUnit = getStockUnit(prod);
    if (!stockUnit) return null;
    const sellingUnit = prod.units.find(u => u.unit_type === ln.unit_label);
    if (!sellingUnit || sellingUnit.id === stockUnit.id) return null;

    const qty = parseFloat(ln.quantity) || 0;
    if (qty <= 0) return null;

    const measureMl = parseFloat(ln.measure_ml) || sellingUnit.conversion_to_base || 0;
    const stockMl = stockUnit.conversion_to_base || 1;
    if (stockMl <= 0 || measureMl <= 0) return null;

    const stockQty = (qty * measureMl / stockMl);
    const stockLabel = stockUnit.unit_type.charAt(0).toUpperCase() + stockUnit.unit_type.slice(1);
    return `= ${stockQty.toFixed(2)} ${stockLabel}${stockQty !== 1 ? 's' : ''}`;
  };

  // Lines calculation — includes stock-unit equivalent and Cost of Sales for each line
  const linesWithTotals = useMemo(() => lines.map(ln => {
    const qty = parseFloat(ln.quantity) || 0;
    const price = parseFloat(ln.unit_selling_price) || 0;
    const lineTotal = qty * price;

    // Compute stock-unit equivalent and Cost of Sales
    let stockEquiv = null;
    let costOfSales = null;
    if (ln.beverage_product_id && qty > 0) {
      const prod = productMap[parseInt(ln.beverage_product_id)];
      if (prod?.units?.length) {
        const stockUnit = getStockUnit(prod);
        const sellingUnit = prod.units.find(u => u.unit_type === ln.unit_label);

        // Determine qty in stock units
        let qtyInStock = qty;
        if (sellingUnit && stockUnit && sellingUnit.id !== stockUnit.id) {
          const measureMl = parseFloat(ln.measure_ml) || sellingUnit.conversion_to_base || 0;
          const stockMl = stockUnit.conversion_to_base || 1;
          if (stockMl > 0 && measureMl > 0) {
            qtyInStock = (qty * measureMl / stockMl);
            const unitName = stockUnit.unit_type.charAt(0).toUpperCase() + stockUnit.unit_type.slice(1);
            stockEquiv = { qty: qtyInStock, label: unitName };
          }
        }

        // Look up cost from cache
        const costKey = `${ln.beverage_product_id}:${header.site_id}`;
        const costData = costCache[costKey];
        if (costData && costData.cost_per_stock_unit > 0) {
          costOfSales = {
            value: qtyInStock * costData.cost_per_stock_unit,
            costPerUnit: costData.cost_per_stock_unit,
            source: costData.cost_source,
            stockUnitLabel: costData.stock_unit_label,
            qtyInStock,
          };
        } else if (costData) {
          costOfSales = { value: 0, costPerUnit: 0, source: costData.cost_source || 'no_cost', stockUnitLabel: costData.stock_unit_label || 'unit', qtyInStock };
        }
      }
    }
    return { ...ln, lineTotal, stockEquiv, costOfSales };
  }), [lines, productMap, costCache, header.site_id]);

  const linesTotalSum = useMemo(
    () => linesWithTotals.reduce((s, ln) => s + ln.lineTotal, 0),
    [linesWithTotals]
  );

  const vatRate = (settings.default_vat_rate || 0) / 100;
  const salesPriceBasis = settings.sales_price_basis || 'gross';
  const vatLabel = salesPriceBasis === 'gross' ? 'incl. VAT' : 'excl. VAT';

  const vatBreakdown = useMemo(() => {
    if (salesPriceBasis === 'gross') {
      const gross = linesTotalSum;
      const net = gross / (1 + vatRate);
      return { net, vat: gross - net, gross };
    }
    const net = linesTotalSum;
    const vat = net * vatRate;
    return { net, vat, gross: net + vat };
  }, [linesTotalSum, vatRate, salesPriceBasis]);

  // Total Cost of Sales across all lines
  const totalCostOfSales = useMemo(
    () => linesWithTotals.reduce((s, ln) => s + (ln.costOfSales?.value || 0), 0),
    [linesWithTotals]
  );

  // Line handlers — auto-pick unit on product selection
  const updateLine = (idx, field, value) => {
    setLines(prev => prev.map((ln, i) => {
      if (i !== idx) return ln;
      const updated = { ...ln, [field]: value };
      if (field === 'beverage_product_id' && value) {
        const prod = productMap[parseInt(value)];
        if (prod) {
          const defaults = getDefaultSellingUnit(prod);
          updated.unit_label = defaults.unit;
          updated.measure_ml = defaults.measure;
        }
      }
      // When unit changes, update measure_ml to that unit's default
      if (field === 'unit_label' && value && ln.beverage_product_id) {
        const prod = productMap[parseInt(ln.beverage_product_id)];
        if (prod?.units) {
          const u = prod.units.find(x => x.unit_type === value);
          if (u?.conversion_to_base) updated.measure_ml = String(u.conversion_to_base);
        }
      }
      return updated;
    }));
  };
  const addLine = () => setLines(prev => [...prev, emptyLine()]);
  const removeLine = (idx) => setLines(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : [emptyLine()]);

  const getProductName = (prodId) => {
    const p = productMap[parseInt(prodId)];
    return p ? `${p.product_code ? p.product_code + ' - ' : ''}${p.product_name}` : '';
  };

  // Save — returns saleId for chaining
  const handleSave = async (showToast = true) => {
    if (!header.site_id) { toast({ title: 'Validation', description: 'Please select a site', variant: 'destructive' }); return null; }
    if (!header.sales_date) { toast({ title: 'Validation', description: 'Please enter a sales date', variant: 'destructive' }); return null; }
    const validLines = lines.filter(ln => ln.beverage_product_id && parseFloat(ln.quantity));
    if (validLines.length === 0) { toast({ title: 'Validation', description: 'Add at least one line item', variant: 'destructive' }); return null; }

    setSaving(true);
    try {
      const headerPayload = {
        site_id: Number(header.site_id),
        reference_number: header.reference_number || null,
        sales_date: header.sales_date,
        period_from: header.period_from || null,
        period_to: header.period_to || null,
        notes: header.notes || null,
      };

      let saleId;
      if (isNew) {
        const created = await salesService.create(headerPayload);
        saleId = created.id;
      } else {
        await salesService.update(Number(id), headerPayload);
        saleId = Number(id);
      }

      const linePayloads = validLines.map(ln => ({
        beverage_product_id: Number(ln.beverage_product_id),
        quantity: parseFloat(ln.quantity),
        unit_selling_price: parseFloat(ln.unit_selling_price) || 0,
        unit_label: ln.unit_label || null,
        measure_ml: ln.measure_ml ? parseFloat(ln.measure_ml) : null,
        notes: ln.notes || null,
      }));
      const updated = await salesService.saveLines(saleId, linePayloads);
      setSale(updated);
      if (showToast) toast({ title: 'Saved', description: 'Sale saved successfully' });

      if (isNew) {
        navigate(`/stocktaking/sales/${saleId}`, { replace: true });
      } else if (updated.lines?.length > 0) {
        setLines(updated.lines.map(ln => ({
          beverage_product_id: String(ln.beverage_product_id),
          quantity: String(ln.quantity),
          unit_selling_price: String(ln.unit_selling_price),
          unit_label: ln.unit_label || '',
          measure_ml: ln.measure_ml ? String(ln.measure_ml) : '',
          notes: ln.notes || '',
        })));
      }
      return saleId;
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save'), variant: 'destructive' });
      return null;
    } finally { setSaving(false); }
  };

  const handlePost = async () => {
    if (!sale?.id) return;
    if (!window.confirm('Post this sale? It will be locked for editing.')) return;
    setPosting(true);
    try {
      const updated = await salesService.post(sale.id);
      setSale(updated);
      toast({ title: 'Posted', description: 'Sale posted successfully' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to post'), variant: 'destructive' });
    } finally { setPosting(false); }
  };

  const handleSaveAndPost = async () => {
    if (!window.confirm('Save and post this sale?')) return;
    setSaveAndPosting(true);
    try {
      const savedId = await handleSave(false);
      if (!savedId) { setSaveAndPosting(false); return; }
      const updated = await salesService.post(savedId);
      setSale(updated);
      toast({ title: 'Saved & Posted', description: 'Sale saved and posted successfully' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to save & post'), variant: 'destructive' });
    } finally { setSaveAndPosting(false); }
  };

  const handleUnpost = async () => {
    if (!sale?.id) return;
    if (!window.confirm('Unpost this sale? It will return to Draft status.')) return;
    setPosting(true);
    try {
      const updated = await salesService.unpost(sale.id);
      setSale(updated);
      toast({ title: 'Unposted', description: 'Sale returned to Draft' });
    } catch (err) {
      toast({ title: 'Error', description: getApiError(err, 'Failed to unpost'), variant: 'destructive' });
    } finally { setPosting(false); }
  };

  if (loading) {
    return <div className="flex justify-center items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>;
  }

  const isBusy = saving || posting || saveAndPosting;

  return (
    <div className="space-y-6" data-testid="sales-entry-page">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/stocktaking/sales')} data-testid="back-to-register">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ShoppingCart className="h-6 w-6 text-slate-600" />
              {isNew ? 'New Sale' : `Sale #${id}`}
            </h1>
            {sale && (
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={isPosted ? 'default' : 'secondary'} className={isPosted ? 'bg-emerald-100 text-emerald-800' : ''}>
                  {isPosted ? 'Posted' : 'Draft'}
                </Badge>
                {sale.created_by_name && <span className="text-xs text-muted-foreground">by {sale.created_by_name}</span>}
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {isPosted ? (
            <Button variant="outline" onClick={handleUnpost} disabled={isBusy} data-testid="unpost-sale-btn">
              <Undo2 className="h-4 w-4 mr-2" /> Unpost
            </Button>
          ) : (
            <>
              <Button onClick={() => handleSave(true)} disabled={isBusy} variant="outline" data-testid="save-sale-btn">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save
              </Button>
              <Button onClick={handleSaveAndPost} disabled={isBusy}
                className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-post-sale-btn">
                {saveAndPosting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Save & Post
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Header Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Sale Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-1.5">
              <Label>Site *</Label>
              <SitePicker sites={sites} value={header.site_id} onChange={v => setHeader(h => ({ ...h, site_id: v }))} disabled={isPosted} testId="site-select" />
            </div>
            <div className="space-y-1.5">
              <Label>Reference</Label>
              <Input value={header.reference_number} onChange={e => setHeader(h => ({ ...h, reference_number: e.target.value }))}
                disabled={isPosted} placeholder="e.g. POS-WK26" data-testid="reference-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Sales Date *</Label>
              <Input type="date" value={header.sales_date} onChange={e => setHeader(h => ({ ...h, sales_date: e.target.value }))}
                disabled={isPosted} data-testid="sales-date-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Period From</Label>
              <Input type="date" value={header.period_from} onChange={e => setHeader(h => ({ ...h, period_from: e.target.value }))}
                disabled={isPosted} data-testid="period-from-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Period To</Label>
              <Input type="date" value={header.period_to} onChange={e => setHeader(h => ({ ...h, period_to: e.target.value }))}
                disabled={isPosted} data-testid="period-to-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Notes</Label>
              <Input value={header.notes} onChange={e => setHeader(h => ({ ...h, notes: e.target.value }))}
                disabled={isPosted} placeholder="Optional notes..." data-testid="notes-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lines Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Line Items</CardTitle>
            <div className="text-lg font-bold">{currencySymbol}{linesTotalSum.toFixed(2)}</div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[28%]">Product</TableHead>
                <TableHead className="w-[60px]">Qty</TableHead>
                <TableHead className="w-[110px]">Selling Unit</TableHead>
                <TableHead className="w-[80px]">Measure (ml)</TableHead>
                <TableHead className="w-[100px]">Selling Price ({currencySymbol}) <span className="text-[10px] font-normal text-muted-foreground block">{vatLabel}</span></TableHead>
                <TableHead className="text-right w-[100px]">Line Total</TableHead>
                <TableHead className="text-right w-[130px]">
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="inline-flex items-center gap-1 cursor-help">
                          Cost of Sales ({currencySymbol}) <Info className="h-3 w-3 text-muted-foreground" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="max-w-xs text-xs">
                        <p>Qty in stock units x WAC cost per stock unit.</p>
                        <p className="mt-1">WAC = Weighted Average Cost from posted deliveries up to the sales date.</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableHead>
                <TableHead className="w-[40px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {linesWithTotals.map((ln, idx) => {
                const unitOptions = getUnitOptions(ln.beverage_product_id);
                const conversionNote = getConversionNote(ln);
                const prod = ln.beverage_product_id ? productMap[parseInt(ln.beverage_product_id)] : null;
                const stockUnit = getStockUnit(prod);
                const sellingUnit = prod?.units?.find(u => u.unit_type === ln.unit_label);
                // Show measure_ml input ONLY when selling in a different unit than the stock unit
                // (e.g. shots of a bottle). Hide for 1:1 cases (bottle = bottle) or single-unit products.
                const showMeasure = sellingUnit && stockUnit
                  && sellingUnit.id !== stockUnit.id
                  && sellingUnit.conversion_to_base;
                const cos = ln.costOfSales;
                const costSourceLabel = cos?.source === 'weighted_average' ? 'WAC'
                  : cos?.source === 'latest_delivery' ? 'Last Delivery'
                  : cos?.source === 'reference_cost' ? 'Ref Cost'
                  : cos?.source === 'no_cost' ? 'No cost data'
                  : '';
                return (
                  <TableRow key={idx} data-testid={`sale-line-${idx}`}>
                    <TableCell>
                      {isPosted ? (
                        <span className="text-sm">{getProductName(ln.beverage_product_id)}</span>
                      ) : (
                        <ProductPicker
                          products={products}
                          value={ln.beverage_product_id}
                          onChange={val => updateLine(idx, 'beverage_product_id', val)}
                          disabled={isPosted}
                          testId={`product-picker-${idx}`}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="0.01" min="0" placeholder="0"
                        value={ln.quantity} onChange={e => updateLine(idx, 'quantity', e.target.value)}
                        disabled={isPosted} className="w-[60px] h-8" data-testid={`qty-input-${idx}`} />
                    </TableCell>
                    <TableCell>
                      {isPosted ? (
                        <span className="text-sm capitalize">{ln.unit_label || '-'}</span>
                      ) : unitOptions.length > 0 ? (
                        <Select value={ln.unit_label || 'none'} onValueChange={v => updateLine(idx, 'unit_label', v === 'none' ? '' : v)}>
                          <SelectTrigger className="h-8 text-xs" data-testid={`unit-select-${idx}`}>
                            <SelectValue placeholder="Unit" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">--</SelectItem>
                            {unitOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <Input placeholder="Unit" value={ln.unit_label}
                          onChange={e => updateLine(idx, 'unit_label', e.target.value)}
                          disabled={isPosted} className="w-20 h-8 text-xs" data-testid={`unit-input-${idx}`} />
                      )}
                    </TableCell>
                    <TableCell>
                      {showMeasure && !isPosted ? (
                        <div>
                          <Input type="number" step="1" min="1" placeholder="ml"
                            value={ln.measure_ml}
                            onChange={e => updateLine(idx, 'measure_ml', e.target.value)}
                            className="w-[72px] h-8 text-xs" data-testid={`measure-input-${idx}`} />
                          {conversionNote && (
                            <p className="text-[10px] text-blue-600 mt-0.5 font-medium whitespace-nowrap">{conversionNote}</p>
                          )}
                        </div>
                      ) : showMeasure && isPosted ? (
                        <span className="text-xs text-muted-foreground">{ln.measure_ml || sellingUnit?.conversion_to_base}ml</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Input type="number" step="0.01" min="0" placeholder="0.00"
                        value={ln.unit_selling_price} onChange={e => updateLine(idx, 'unit_selling_price', e.target.value)}
                        disabled={isPosted} className="w-24 h-8" data-testid={`price-input-${idx}`} />
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="font-medium">{currencySymbol}{ln.lineTotal.toFixed(2)}</span>
                      {ln.stockEquiv && (
                        <p className="text-[10px] text-blue-600 font-medium whitespace-nowrap mt-0.5">
                          = {ln.stockEquiv.qty.toFixed(2)} {ln.stockEquiv.label}{ln.stockEquiv.qty !== 1 ? 's' : ''}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-right" data-testid={`cos-cell-${idx}`}>
                      {cos ? (
                        <div>
                          <span className={`font-medium ${cos.value > 0 ? 'text-slate-900' : 'text-muted-foreground'}`}>
                            {cos.value > 0 ? `${currencySymbol}${cos.value.toFixed(2)}` : '-'}
                          </span>
                          {cos.value > 0 && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <p className="text-[10px] text-amber-600 font-medium mt-0.5 cursor-help whitespace-nowrap">
                                    {costSourceLabel}
                                    {cos.qtyInStock ? ` (${cos.qtyInStock.toFixed(2)} x ${currencySymbol}${cos.costPerUnit.toFixed(2)})` : ''}
                                  </p>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="max-w-xs text-xs">
                                  <p><strong>{costSourceLabel}</strong>: {currencySymbol}{cos.costPerUnit.toFixed(4)} per {cos.stockUnitLabel}</p>
                                  <p>{cos.qtyInStock.toFixed(4)} {cos.stockUnitLabel}s x {currencySymbol}{cos.costPerUnit.toFixed(4)} = {currencySymbol}{cos.value.toFixed(2)}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                          {cos.source === 'no_cost' && (
                            <p className="text-[10px] text-red-500 font-medium mt-0.5">No cost data</p>
                          )}
                        </div>
                      ) : ln.beverage_product_id ? (
                        <span className="text-xs text-muted-foreground">...</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {!isPosted && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500"
                          onClick={() => removeLine(idx)} data-testid={`remove-line-${idx}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {!isPosted && (
            <div className="p-3 border-t">
              <Button variant="outline" size="sm" onClick={addLine} data-testid="add-line-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Line
              </Button>
            </div>
          )}

          <Separator />
          <div className="flex justify-end p-4">
            <div className="w-full max-w-sm space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Lines Total <span className="text-xs">({vatLabel})</span></span>
                <span className="font-mono font-medium">{currencySymbol}{linesTotalSum.toFixed(2)}</span>
              </div>
              {vatRate > 0 && (
                <>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">VAT ({settings.default_vat_rate}%)</span>
                    <span className="font-mono text-muted-foreground">{currencySymbol}{vatBreakdown.vat.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-semibold">
                    <span>{salesPriceBasis === 'gross' ? 'Net' : 'Gross'} Total</span>
                    <span className="font-mono">{currencySymbol}{salesPriceBasis === 'gross' ? vatBreakdown.net.toFixed(2) : vatBreakdown.gross.toFixed(2)}</span>
                  </div>
                </>
              )}
              {totalCostOfSales > 0 && (
                <>
                  <Separator className="my-1" />
                  <div className="flex justify-between text-sm">
                    <span className="text-amber-700 font-medium">Total Cost of Sales</span>
                    <span className="font-mono font-semibold text-amber-700">{currencySymbol}{totalCostOfSales.toFixed(2)}</span>
                  </div>
                  {vatBreakdown.net > 0 && (
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground">Gross Profit (est.)</span>
                      <span className="font-mono text-muted-foreground">{currencySymbol}{(vatBreakdown.net - totalCostOfSales).toFixed(2)}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
